import { useEffect, useState, JSX, useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useDynatrace } from '@src/hooks/use-dynatrace.hook';
import { ConnectedStaticLandingPage } from '@src/pages/landing';
import { CmsLandingPage } from '@src/pages/landing/components/cms/page';
import {
  GetGeneralPageApiRequestParams,
  GetGeneralPageApiResponse,
} from '@src/@types/cms';
import { useCmsApi } from '@src/hooks/use-cms-api.hook';
import { useAbTest } from '@src/hooks/use-ab-test.hook';
import WindowService from '@src/services/window.service';
import { ServerConfigContext } from '@src/contexts/server-config-context';
import { ChewyAnalyticsWrapper } from '@src/components/analytics/chewy-analytics-wrapper';
import { ChewyCarePlusLogo } from '@src/components/icons/chewy-heath-careplus';

interface ComponentState {
  cmsLandingPageContent: GetGeneralPageApiResponse;
  getGeneralPageRequestHandled: boolean;
}

/**
 * Controls the variant of landing page rendered contingent on
 * the experiment and variation that a user is opted into.
 *
 * @link https://chewyinc.atlassian.net/wiki/spaces/PetInsurance/pages/2193719609/NW-4360+Integrating+PI+SPA+with+CMS
 */
export const LandingPage = (): JSX.Element => {
  const { reportError } = useDynatrace();
  const [state, setState] = useState<ComponentState>({
    cmsLandingPageContent: {
      metadata: null,
      zones: [],
    },
    getGeneralPageRequestHandled: false,
  });

  const {
    enabled: abTestingEnabled,
    isCurrentVariation,
    activate,
    initialized: abTestingInitialized,
    initializationError: abTestingInitializationError,
  } = useAbTest('2025_04_14_CAREPLUS_LP_TEST');

  const isAbTestActive =
    abTestingEnabled && abTestingInitialized && !abTestingInitializationError;

  const { isReady, getGeneralPage } = useCmsApi();

  useEffect(() => {
    // Default to the URL variant if given
    let cmsVariant = WindowService.window.location.pathname.split('/')[2];
    // If no URL variant is set, and the test is active, and the user is in the treatment group, force the variant to 2025
    if (!cmsVariant && isAbTestActive && isCurrentVariation('TREATMENT_1')) {
      cmsVariant = '2025';
    }
    const requestParams: GetGeneralPageApiRequestParams = {
      generalType: 'pet-insurance',
      ...(cmsVariant && { selectionKey: cmsVariant }),
    };

    /**
     * Wait to execute getGeneralPage fetch request and thereby
     * rendering of the landing page until user context has
     * either loaded or errored via isReady from {@link useCmsApi}.
     * We also must wait until the A/B testing client is initialized
     * (unless the experiment is disabled.)
     */
    if (isReady && (abTestingInitialized || !abTestingEnabled)) {
      getGeneralPage(requestParams)
        .then((results) => {
          setState((prev) => ({
            ...prev,
            cmsLandingPageContent: results,
            getGeneralPageRequestHandled: true,
          }));
        })
        .catch((e: Error) => {
          reportError('Error loading CMS landing page');
          reportError(e);

          // A fail-safe in the event where something went
          // awry with the API itself prior to receiving
          // a response.
          setState((prev) => ({
            ...prev,
            getGeneralPageRequestHandled: true,
          }));
        });
    }
  }, [
    isReady,
    getGeneralPage,
    abTestingInitialized,
    abTestingEnabled,
    abTestingInitializationError,
    isCurrentVariation,
  ]);

  // Default to static landing page
  let LandingPageVariant = <ConnectedStaticLandingPage />;

  // Fallback to static landing page if CMS content is empty/missing
  // or A/B initialization/experiment does not permit CMS landing page
  const shouldDisplayCmsLandingPage =
    state.cmsLandingPageContent.zones.length > 0 &&
    state.cmsLandingPageContent.zones[0]?.components?.length > 0;

  //toggle for chewy analytics ale wrapper just a safe way to turn it off/on
  const { chewyAnalyticsAleEnabled, headerEnv, appVersion, awsRegionShort } =
    useContext(ServerConfigContext);

  // Activate analytics for CMS experiment only if CMS landing page
  // will be rendered
  if (shouldDisplayCmsLandingPage) {
    LandingPageVariant = (
      <ErrorBoundary
        onError={(e) => {
          reportError('Error showing CMS landing page');
          reportError(e);
        }}
        FallbackComponent={ConnectedStaticLandingPage}
      >
        {chewyAnalyticsAleEnabled ? (
          <ChewyAnalyticsWrapper
            appVersion={appVersion}
            awsRegionShort={awsRegionShort}
            environment={headerEnv}
          >
            <CmsLandingPage
              cmsLandingPageContent={state.cmsLandingPageContent}
              activate={isAbTestActive ? activate : undefined}
            />
          </ChewyAnalyticsWrapper>
        ) : (
          <CmsLandingPage
            cmsLandingPageContent={state.cmsLandingPageContent}
            activate={isAbTestActive ? activate : undefined}
          />
        )}
      </ErrorBoundary>
    );
  }

  // Show the landing page only when it's determined which landing page to show
  const shouldRenderLandingPage = state.getGeneralPageRequestHandled;
  return shouldRenderLandingPage && LandingPageVariant;
};
